import React from "react";
import { Plus, Inbox, FileText, Zap, Bell, BarChart2, ArrowRight, WifiOff, MousePointer2 } from "lucide-react";
import { Button } from "./ui/button";
import SetupDetailView from "./SetupDetailView";
import PortfolioStatsBar from "./PortfolioStatsBar";
import SetupCards from "./SetupCards";
import AlertsPanel from "./AlertsPanel";

export default function Dashboard({ setups, selectedId, onSelect, onAddSetup, onEditSetup, onRefresh, loading = false, backendError = false }) {
  // Initial load skeleton
  if (loading && setups.length === 0) {
    return <DashboardSkeleton />;
  }

  // Critical backend error with no cached data
  if (!loading && backendError && setups.length === 0) {
    return <BackendErrorState onRetry={onRefresh} />;
  }

  // Empty state — no setups at all
  if (!loading && setups.length === 0) {
    return <EmptyState onAdd={onAddSetup} />;
  }

  return (
    <div className="flex flex-col min-h-full" data-testid="dashboard-page">
      <PortfolioStatsBar setups={setups} />
      <div className="p-4 md:p-6 lg:p-8 space-y-6 max-w-full overflow-x-hidden">
        <AlertsPanel setups={setups} />
        {selectedId ? (
          <SetupDetailView
            setupId={selectedId}
            onDeleted={() => onRefresh?.()}
            onChanged={() => onRefresh?.()}
            onEdit={onEditSetup}
            embedded
          />
        ) : (
          <NoSelectionPlaceholder setups={setups} onSelect={onSelect} />
        )}
      </div>
    </div>
  );
}

// ─── Skeleton ────────────────────────────────────────────────────────────────

function DashboardSkeleton() {
  return (
    <div className="flex flex-col min-h-full animate-pulse" data-testid="dashboard-skeleton">
      {/* Stats bar skeleton */}
      <div className="border-b border-zinc-800 px-4 md:px-6 py-3 flex gap-4 overflow-x-auto">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="shrink-0 space-y-1.5">
            <div className="h-2.5 bg-zinc-800 rounded w-20" />
            <div className="h-4 bg-zinc-800 rounded w-14" />
          </div>
        ))}
      </div>
      {/* Cards skeleton */}
      <div className="p-4 md:p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="border border-zinc-800 bg-zinc-950 p-4 space-y-3 h-36">
              <div className="flex items-start justify-between">
                <div className="space-y-1.5">
                  <div className="h-4 bg-zinc-800 rounded w-28" />
                  <div className="h-2.5 bg-zinc-800/60 rounded w-16" />
                </div>
                <div className="h-4 bg-zinc-800 rounded w-12" />
              </div>
              <div className="flex items-end justify-between pt-2">
                <div className="h-6 bg-zinc-800 rounded w-20" />
                <div className="h-3 bg-zinc-800/60 rounded w-14" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Backend error (critical) ─────────────────────────────────────────────────

function BackendErrorState({ onRetry }) {
  return (
    <div className="min-h-[70vh] flex flex-col items-center justify-center px-4 py-16 text-center" data-testid="backend-error-state">
      <div className="w-14 h-14 border border-rose-800 flex items-center justify-center mb-6">
        <WifiOff size={24} className="text-rose-500" />
      </div>
      <div className="font-display font-black text-2xl tracking-tight mb-3">Backend nicht erreichbar</div>
      <p className="text-muted-foreground text-sm max-w-sm leading-relaxed mb-2">
        Die Verbindung zum Server konnte nicht hergestellt werden. Bitte prüfe ob der Backend-Dienst läuft.
      </p>
      <p className="text-zinc-600 text-xs mb-8 font-mono">GET /api/setups → Verbindung fehlgeschlagen</p>
      <Button
        onClick={onRetry}
        variant="outline"
        className="border-zinc-700 bg-transparent hover:bg-zinc-800 font-display font-bold uppercase tracking-wider text-xs h-10 px-6"
      >
        Erneut versuchen
      </Button>
    </div>
  );
}

// ─── No selection placeholder ────────────────────────────────────────────────

function NoSelectionPlaceholder({ setups, onSelect }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="no-selection-placeholder">
      <div className="w-12 h-12 border border-zinc-700 flex items-center justify-center mb-4">
        <MousePointer2 size={20} className="text-zinc-500" />
      </div>
      <div className="font-display font-bold text-base tracking-tight text-zinc-300 mb-1">
        Setup auswählen
      </div>
      <p className="text-zinc-600 text-xs max-w-xs leading-relaxed">
        Wähle ein Setup aus der Watchlist links oder klicke eine Karte unten.
      </p>
      {setups.length > 0 && (
        <div className="mt-8 w-full max-w-2xl">
          <SetupCards setups={setups.slice(0, 6)} onSelect={onSelect} />
        </div>
      )}
    </div>
  );
}

// ─── Empty state ──────────────────────────────────────────────────────────────

const FEATURES = [
  { icon: FileText, title: "Report einlesen", desc: "Friedrich-Report URL oder E-Mail-Text einfügen — KI extrahiert Zonen, Targets und Stop-Loss automatisch." },
  { icon: Bell,     title: "Automatische Alerts", desc: "Sobald ein Asset eine Entry-Zone erreicht, bekommst du einen Telegram-Alert in Echtzeit." },
  { icon: Zap,      title: "Live Preise", desc: "Alle Instrumente mit aktuellem Preis, Tagesveränderung und Zone-Nähe auf einen Blick." },
  { icon: BarChart2,title: "Makro Dashboard", desc: "Shiller CAPE, Buffett-Indikator und Fed Rate — immer im Blick behalten." },
];

function EmptyState({ onAdd }) {
  return (
    <div className="min-h-[80vh] flex flex-col items-center justify-center px-4 py-16" data-testid="empty-state">
      {/* Logo mark */}
      <div className="flex items-center gap-3 mb-8">
        <div className="w-10 h-10 bg-foreground text-background flex items-center justify-center font-display font-black text-xl tracking-tighter">FT</div>
        <div className="font-display font-black text-2xl tracking-tight">
          FRIEDRICH<span className="text-muted-foreground">·TRADE</span>
        </div>
      </div>

      {/* Headline */}
      <div className="text-center max-w-lg mb-10">
        <h2 className="font-display font-black text-3xl tracking-tight mb-3">
          Willkommen im Allocation Hub
        </h2>
        <p className="text-muted-foreground text-sm leading-relaxed">
          Lege dein erstes Setup an — per Friedrich-Report, E-Mail oder manuell.
          Dann übernimmt die Plattform: Live-Preise, Zonen-Alerts, Charts.
        </p>
      </div>

      {/* CTA */}
      <Button
        onClick={onAdd}
        className="bg-foreground text-background hover:opacity-90 rounded-sm font-display font-bold uppercase tracking-wider h-11 px-8 mb-12 text-sm"
        data-testid="btn-empty-add"
      >
        <Plus size={15} className="mr-2" /> Erstes Setup anlegen
      </Button>

      {/* Feature grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-2xl">
        {FEATURES.map(({ icon: Icon, title, desc }) => (
          <div key={title} className="border border-border bg-card p-4 flex gap-3">
            <div className="mt-0.5 shrink-0">
              <div className="w-7 h-7 border border-border flex items-center justify-center">
                <Icon size={13} className="text-muted-foreground" />
              </div>
            </div>
            <div>
              <div className="font-display font-bold text-xs uppercase tracking-wider text-foreground mb-1">{title}</div>
              <p className="text-muted-foreground text-xs leading-relaxed">{desc}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Hint */}
      <div className="mt-8 flex items-center gap-2 text-xs text-muted-foreground">
        <ArrowRight size={12} />
        <span>Oder gehe zu <strong className="text-foreground">Einstellungen</strong> um Telegram-Alerts einzurichten</span>
      </div>
    </div>
  );
}
